// src/services/toolsAPI/fileReducerAPI.ts
import tokenManager from '../../utils/tokenManager';

// Use environment variable with fallback to localhost for development
const API_BASE = import.meta.env.VITE_API_URL || 'http://localhost:8000';

// Define all API paths as absolute URLs
const DIRECT_ROOT_UPLOAD = `${API_BASE}/direct-reducer-upload`;

export interface UploadResponse {
    fileId: string;
    fileName: string;
    fileType: string;
}

export interface ReducerOptions {
    quality?: number;
    removeImages?: boolean;
    flatten?: boolean;
    [key: string]: any;
}

export interface ReducerResponse {
    resultId: string;
    fileName: string;
    originalSize: number;
    newSize: number;
    sizeReduction: number;
    percentReduction: number;
}



// Create demo mode helper function
const isDemoMode = (): boolean => {
    return (
        import.meta.env.VITE_DEMO_MODE === 'true' || window.location.search.includes('demo=true')
    );
};

// Create demo response functions
const createDemoResponse = (fileId: string): ReducerResponse => {
    return {
        resultId: `demo-reducer-${Date.now()}`,
        fileName: 'document_FileFusion.pdf',
        originalSize: 1024 * 1024,
        newSize: 512 * 1024,
        sizeReduction: 512 * 1024,
        percentReduction: 50,
    };
};

/**
 * Uploads a file to the server for size reduction processing
 * Auto-routes large files (>2MB) through chunked parallel upload
 * @param file - The file to upload
 * @param onProgress - Optional callback with upload percentage (0-100)
 * @returns The server response with file ID
 */
export const uploadFile = async (
    file: File,
    onProgress?: (percent: number) => void
): Promise<UploadResponse> => {
    // For demo or development mode
    if (isDemoMode()) {
        await new Promise((resolve) => setTimeout(resolve, 1000));
        return {
            fileId: `demo-file-${Date.now()}`,
            fileName: file.name,
            fileType: file.type,
        };
    }

    const CHUNKED_THRESHOLD = 2 * 1024 * 1024; // 2MB

    // Route large files to chunked upload
    if (file.size > CHUNKED_THRESHOLD) {
        return uploadFileChunked(file, onProgress);
    }

    // Small files: single request (existing logic)
    const formData = new FormData();
    formData.append('file', file);
    formData.append('timestamp', Date.now().toString());

    try {
        // Try the API path with absolute URL first for better security
        try {
            const absoluteUrl = `${API_BASE}/api/tools/reducer/upload`;
            const securityHeaders = (await tokenManager.getSecurityHeader()) || {};
            const apiResponse = await fetch(absoluteUrl, {
                method: 'POST',
                headers: securityHeaders as HeadersInit,
                body: formData,
            });

            if (apiResponse.ok) {
                onProgress?.(100);
                return await apiResponse.json();
            }
        } catch (apiError) {
            // Silently fail to fallback
        }

        // Try direct root upload endpoint as fallback
        try {
            const securityHeaders = (await tokenManager.getSecurityHeader()) || {};
            const directRootResponse = await fetch(DIRECT_ROOT_UPLOAD, {
                method: 'POST',
                headers: securityHeaders as HeadersInit,
                body: formData,
            });

            if (directRootResponse.ok) {
                onProgress?.(100);
                return await directRootResponse.json();
            }
        } catch (directError) {
            // Silently fail to fallback
        }

        // Fallback to relative URL
        try {
            const securityHeaders = (await tokenManager.getSecurityHeader()) || {};
            const response = await fetch(`/api/tools/reducer/upload`, {
                method: 'POST',
                headers: securityHeaders as HeadersInit,
                body: formData,
            });

            if (!response.ok) {
                const errorText = await response.text();
                console.error('UPLOAD ERROR - Fallback failed:', response.status, errorText);
                throw new Error(`Upload failed (${response.status}): ${errorText || 'Unknown error'}`);
            }

            onProgress?.(100);
            return await response.json();
        } catch (fallbackError) {
            console.error('UPLOAD ERROR - Fallback failed:', fallbackError);
            throw fallbackError;
        }
    } catch (error: any) {
        console.error('UPLOAD ERROR - Final error:', error);

        // In development mode, return mock data if all uploads fail
        if (import.meta.env.MODE === 'development') {
            return {
                fileId: `mock-file-${Date.now()}`,
                fileName: file.name,
                fileType: file.type,
            };
        }

        throw error;
    }
};

/**
 * Chunked parallel upload for large files (>2MB)
 * Splits file into 2MB chunks and uploads 4 at a time
 */
const uploadFileChunked = async (
    file: File,
    onProgress?: (percent: number) => void
): Promise<UploadResponse> => {
    const CHUNK_SIZE = 2 * 1024 * 1024; // 2MB per chunk
    const MAX_CONCURRENT = 4; // 4 parallel uploads

    const securityHeaders = (await tokenManager.getSecurityHeader()) || {};
    const baseUrl = `${API_BASE}/api/tools/reducer`;

    // 1. Initialize upload session
    const initForm = new FormData();
    initForm.append('fileName', file.name);
    initForm.append('fileSize', file.size.toString());
    initForm.append('fileType', file.type);

    const initResponse = await fetch(`${baseUrl}/chunked-upload/init`, {
        method: 'POST',
        headers: securityHeaders as HeadersInit,
        body: initForm,
    });

    if (!initResponse.ok) {
        throw new Error(`Chunked upload init failed: ${initResponse.status}`);
    }

    const { uploadId } = await initResponse.json();
    const totalChunks = Math.ceil(file.size / CHUNK_SIZE);
    let completedChunks = 0;

    // 2. Upload chunks in parallel (batches of MAX_CONCURRENT)
    const uploadChunk = async (chunkIndex: number) => {
        const start = chunkIndex * CHUNK_SIZE;
        const end = Math.min(file.size, start + CHUNK_SIZE);
        const chunkBlob = file.slice(start, end);

        const chunkForm = new FormData();
        chunkForm.append('chunk', chunkBlob, `chunk_${chunkIndex}`);
        chunkForm.append('uploadId', uploadId);
        chunkForm.append('chunkIndex', chunkIndex.toString());

        const response = await fetch(`${baseUrl}/chunked-upload/chunk`, {
            method: 'POST',
            headers: securityHeaders as HeadersInit,
            body: chunkForm,
        });

        if (!response.ok) {
            throw new Error(`Chunk ${chunkIndex} upload failed: ${response.status}`);
        }

        completedChunks++;
        onProgress?.(Math.round((completedChunks / totalChunks) * 95)); // Reserve 5% for finalize
    };

    // Process in batches of MAX_CONCURRENT
    for (let i = 0; i < totalChunks; i += MAX_CONCURRENT) {
        const batch = [];
        for (let j = i; j < Math.min(i + MAX_CONCURRENT, totalChunks); j++) {
            batch.push(uploadChunk(j));
        }
        await Promise.all(batch);
    }

    // 3. Finalize — reassemble on server
    const finalizeForm = new FormData();
    finalizeForm.append('uploadId', uploadId);
    finalizeForm.append('fileName', file.name);
    finalizeForm.append('totalChunks', totalChunks.toString());
    finalizeForm.append('fileType', file.type);

    const finalizeResponse = await fetch(`${baseUrl}/chunked-upload/finalize`, {
        method: 'POST',
        headers: securityHeaders as HeadersInit,
        body: finalizeForm,
    });

    if (!finalizeResponse.ok) {
        throw new Error(`Chunked upload finalize failed: ${finalizeResponse.status}`);
    }

    onProgress?.(100);
    return await finalizeResponse.json();
};

/**
 * Processes an uploaded file with specified options
 * @param fileId - The ID of the uploaded file
 * @param options - Processing options
 * @returns The server response with processing results
 */
export const processFile = async (
    fileId: string,
    options: ReducerOptions
): Promise<ReducerResponse> => {
    // For demo or development mode
    if (isDemoMode() || fileId.startsWith('demo-') || fileId.startsWith('mock-')) {
        await new Promise((resolve) => setTimeout(resolve, 1500));
        return createDemoResponse(fileId);
    }

    const formData = new FormData();
    formData.append('file_id', fileId);
    formData.append('options', JSON.stringify(options));

    // Add branding parameter for FileFusion naming
    formData.append('use_branding', 'true');

    try {
        // Try standardized API call with absolute URL first (like upload)
        try {
            const apiProcessUrl = `${API_BASE}/api/tools/reducer/process`;

            const securityHeaders = (await tokenManager.getSecurityHeader()) || {};
            const response = await fetch(apiProcessUrl, {
                method: 'POST',
                headers: securityHeaders as HeadersInit,
                body: formData,
            });

            if (response.ok) {
                const data = await response.json();

                // Add extra validation for resultId
                if (!data || !data.resultId) {
                    console.error('PROCESS ERROR - Response missing resultId:', data);
                    throw new Error('Processing completed but no result ID was returned');
                }

                return data;
            }
        } catch (apiError) {
            // Silently fail to fallback
        }

        // Try the direct process endpoint as fallback
        try {
            const directProcessUrl = `${API_BASE}/direct-reducer-process`;
            const securityHeaders = (await tokenManager.getSecurityHeader()) || {};

            const directResponse = await fetch(directProcessUrl, {
                method: 'POST',
                headers: securityHeaders as HeadersInit,
                body: formData,
            });

            if (directResponse.ok) {
                const data = await directResponse.json();

                // Add extra validation for resultId
                if (!data || !data.resultId) {
                    console.error('PROCESS ERROR - Response missing resultId:', data);
                    throw new Error('Processing completed but no result ID was returned');
                }

                return data;
            }
        } catch (directError) {
            // Silently fail to fallback
        }

        // Final fallback to relative URL
        const relativeUrl = `/api/tools/reducer/process`;
        const securityHeaders = (await tokenManager.getSecurityHeader()) || {};

        const fallbackResponse = await fetch(relativeUrl, {
            method: 'POST',
            headers: securityHeaders as HeadersInit,
            body: formData,
        });

        if (!fallbackResponse.ok) {
            throw new Error(
                `Server responded with ${fallbackResponse.status}: ${await fallbackResponse.text()}`
            );
        }

        const fallbackData = await fallbackResponse.json();

        // Add extra validation for resultId
        if (!fallbackData || !fallbackData.resultId) {
            console.error('PROCESS ERROR - Response missing resultId:', fallbackData);
            throw new Error('Processing completed but no result ID was returned');
        }

        return fallbackData;
    } catch (error: any) {
        console.error('PROCESS ERROR:', error);

        // In development mode, return mock data if processing fails
        if (import.meta.env.MODE === 'development') {
            return createDemoResponse(fileId);
        }

        throw new Error(error.message || 'Processing failed');
    }
};

/**
 * Gets the download URL for a processed file
 * @param resultId - The ID of the result to download
 * @returns The download URL
 */
export const getDownloadLink = (resultId: string): string => {
    if (!resultId) {
        console.error('REDUCER DOWNLOAD ERROR - No resultId provided');
        return '#';
    }

    // For demo or mock resultIds
    if (resultId.startsWith('demo-') || resultId.startsWith('mock-')) {
        return URL.createObjectURL(new Blob(['Demo reduced file content'], { type: 'application/pdf' }));
    }

    // Return absolute URL - using the correct reducer-specific endpoint
    return `${API_BASE}/api/tools/reducer/download/${resultId}`;
};
